import React, { useState } from 'react';
import { Table, Tag, Button, Select, message, Pagination } from 'antd';
import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { ExceptionOrderListItem, ExceptionSubTab, RecipientAddressType } from '../../../../graphql/types/ExceptionOrderTypes';
import { buildFullAddress } from '../../../../utils/addressFormatUtils';
import { formatTimestampToDateTime } from '../../../../utils/dateTimeUtils';
import { BATCH_SET_NEXT_ACTION, BatchSetNextActionResponse, BatchSetNextActionVariables } from '../../../wms/exceptions/graphql/mutations/setNextAction';
import { useStatusInternationalization } from '../../../../pages/delivery/parcels/hooks/useStatusInternationalization';
import { ExceptionDictionaryResponse, GET_ALL_EXCEPTION } from '../../../wms/exceptions/graphql/queries/getAllException';
import { GET_INT_ENUM_VALUES, GetIntEnumValuesResponse } from '../../../../graphql/queries/getEnumValues';
import { ACTION_NAME_MAP } from '../../../../config/filterOptions';
import { showError } from '../../../../utils/exceptionUtils';

interface ExceptionOrderTableProps {
    data: ExceptionOrderListItem[];
    loading: boolean;
    currentTab: string;
    currentSubTab?: ExceptionSubTab;
    pagination: {
        current: number;
        pageSize: number;
        total: number;
        onChange: (page: number, pageSize: number) => void;
    };
    onSelectChange?: (selectedRowKeys: React.Key[], selectedRows: ExceptionOrderListItem[]) => void;
    selectedRowKeys?: React.Key[];
    onRefresh?: () => void;
    showParcelDetail?: (record: ExceptionOrderListItem) => void;
}

const ExceptionOrderTable: React.FC<ExceptionOrderTableProps> = ({
    data,
    loading,
    currentTab,
    currentSubTab,
    pagination,
    onSelectChange,
    selectedRowKeys: externalSelectedRowKeys,
    onRefresh,
    showParcelDetail
}) => {
    const { t } = useTranslation();
    const { getStatusText, getExceptionReasonText, getNextActionTextFromBackend } = useStatusInternationalization();

    // 查询异常字典
    const { data: exceptionData } = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const exceptionList = exceptionData?.getExceptionDictionaryAll || [];

    // 查询枚举值列表（从后端获取）
    const { data: overallStatusEnumData } = useQuery<GetIntEnumValuesResponse>(GET_INT_ENUM_VALUES, {
        variables: { enumType: 'OverallStatus' }
    });
    const { data: exceptionStatusEnumData } = useQuery<GetIntEnumValuesResponse>(GET_INT_ENUM_VALUES, {
        variables: { enumType: 'ExceptionOrderStatus' }
    });

    // 从后端枚举构建 value -> name 映射
    const orderStatusMap = React.useMemo(() => {
        const map: Record<number, string> = {};
        (overallStatusEnumData?.getIntEnumValues || []).forEach(item => {
            map[item.value] = item.name;
        });
        return map;
    }, [overallStatusEnumData]);

    // 根据数字状态码获取状态文本
    const getOrderStatusText = (status: number | undefined): string => {
        if (!status) return '-';
        const statusStr = orderStatusMap[status];
        if (statusStr) {
            return getStatusText(statusStr);
        }
        return String(status);
    };

    // 根据异常原因码获取国际化文本
    const getExceptionReasonTextByCode = (code: string | undefined): string => {
        if (!code) return '-';
        const exception = exceptionList.find(ex => ex.exceptionReasonCode === code);
        if (exception) {
            return getExceptionReasonText(exception, code);
        }
        return code;
    };

    // 内部选择状态（如果没有外部控制）
    const [internalSelectedRowKeys, setInternalSelectedRowKeys] = useState<React.Key[]>([]);
    const selectedRowKeys = externalSelectedRowKeys !== undefined ? externalSelectedRowKeys : internalSelectedRowKeys;

    // 单条编辑状态
    const [editingKey, setEditingKey] = useState<string | null>(null);
    const [editingAction, setEditingAction] = useState<string>('');

    // 批量设置 mutation
    const [batchSetNextAction] = useMutation<BatchSetNextActionResponse, BatchSetNextActionVariables>(BATCH_SET_NEXT_ACTION);

    // 下一步动作选项
    const ACTION_OPTIONS = [
        { value: '1', label: t('config.nextAction.hold') },
        { value: '2', label: t('config.nextAction.reDeliver') },
        { value: '3', label: t('config.nextAction.return') },
        { value: '5', label: t('config.nextAction.reSort') }
    ];

    // 处理选择变化
    const handleSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: ExceptionOrderListItem[]) => {
        if (onSelectChange) {
            onSelectChange(newSelectedRowKeys, selectedRows);
        } else {
            setInternalSelectedRowKeys(newSelectedRowKeys);
        }
    };

    // 配置行选择
    const rowSelection = {
        selectedRowKeys,
        onChange: handleSelectChange,
    };

    // 开始编辑
    const startEdit = (record: ExceptionOrderListItem) => {
        setEditingKey(record.id);
        // 将当前的 nextAction 数字值转换为字符串
        setEditingAction(record.nextAction ? String(record.nextAction) : '');
    };

    // 取消编辑
    const cancelEdit = () => {
        setEditingKey(null);
        setEditingAction('');
    };

    // 保存编辑
    const saveEdit = async (record: ExceptionOrderListItem) => {
        if (!editingAction) {
            message.warning(t('wms.exceptionorder.messages.pleaseSelectValidAction'));
            return;
        }

        // 使用英文 actionName 进行后端请求，与旧页面保持一致
        const actionName = ACTION_NAME_MAP[editingAction] || editingAction;

        try {
            await batchSetNextAction({
                variables: {
                    orderIds: [parseInt(record.orderId)],
                    actionName: actionName
                }
            });

            message.success(t('wms.exceptionorder.messages.nextActionUpdatedSuccessfully', { action: getNextActionTextFromBackend(actionName) }));
            setEditingKey(null);
            setEditingAction('');

            // 刷新数据
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('Failed to update next action:', error);
            showError(error);
        }
    };

    // 异常状态颜色映射
    const EXCEPTION_STATUS_COLOR: Record<number, string> = { 10: 'red', 50: 'orange', 90: 'green' };

    // 从后端枚举构建 value -> name 映射
    const exceptionStatusMap = React.useMemo(() => {
        const map: Record<number, string> = {};
        (exceptionStatusEnumData?.getIntEnumValues || []).forEach(item => {
            map[item.value] = item.name;
        });
        return map;
    }, [exceptionStatusEnumData]);

    // 获取异常状态显示配置
    const getExceptionStatusConfig = (status: number) => {
        const name = exceptionStatusMap[status];
        if (name) {
            return { text: t(`wms.exceptionorder.exceptionStatus.${name}`), color: EXCEPTION_STATUS_COLOR[status] || 'default' };
        }
        return { text: '-', color: 'default' };
    };

    // 获取下一步动作显示文本（使用统一的 ACTION_NAME_MAP）
    const getNextActionText = (action: number | undefined) => {
        if (!action) return '-';
        const actionName = ACTION_NAME_MAP[String(action)];
        if (actionName) {
            return getNextActionTextFromBackend(actionName);
        }
        return '-';
    };

    // 判断是否应该禁止编辑nextAction
    const isNextActionDisabled = (record: ExceptionOrderListItem): boolean => {
        // 1. 异常单已关闭 - 禁止编辑
        if (record.exceptionStatus == 90) {
            return true;
        }

        // 2. 订单终态状态 - 禁止编辑
        // DELIVERED(60), RETURNED(80), RETURN_STARTED(81), DESTROYED(85), MISSING(86), DAMAGED(87)
        const terminalStatuses = [60, 80, 81, 85, 86, 87];
        if (record.orderStatus && terminalStatuses.includes(record.orderStatus)) {
            return true;
        }

        // 3. 终态异常码 - 禁止编辑（兼容历史数据）
        // 与后端 ExceptionDictionaries.TERMINAL_EXCEPTION_CODES 保持一致
        const terminalExceptionCodes = [
            // 丢失类
            'ERHUB003', 'ERHUB005', 'ERHUB008', 'ERWHS003', 'ERWHS004', 'ERDSP003',
            // 破损类
            'ERHUB001', 'ERHUB006', 'ERHUB007', 'ERWHS001', 'ERWHS005', 'ERDSP001'
        ];
        if (record.exceptionReasonCode && terminalExceptionCodes.includes(record.exceptionReasonCode)) {
            return true;
        }

        return false;
    };

    // 根据当前 Tab 动态生成列配置
    const getColumns = (): ColumnsType<ExceptionOrderListItem> => {
        // 判断是否显示「超5天未处理」标签：仅在第一个Tab（仓库疑似丢失）时显示
        // 后端 isOverdue 已限制为 CREATED 状态且超5天
        const shouldShowOverdueTag = (record: ExceptionOrderListItem) => {
            return currentTab === 'WAREHOUSE_SUSPECTED_LOST' && record.isOverdue;
        };

        const commonColumns: ColumnsType<ExceptionOrderListItem> = [
            {
                title: t('wms.exceptions.columns.tracking'),
                dataIndex: 'trackingNumber',
                key: 'trackingNumber',
                align: 'left',
                render: (text, record) => {
                    const showOverdueTag = shouldShowOverdueTag(record);
                    return (
                        <div className="flex items-center gap-2">
                            <span
                                style={{
                                    cursor: showParcelDetail ? 'pointer' : 'default',
                                    color: '#722ed1',
                                }}
                                onClick={() => showParcelDetail && showParcelDetail(record)}
                            >
                                {text}
                            </span>
                            {showOverdueTag && (
                                <Tag color="red">{t('wms.exceptionorder.overFiveDaysUnprocessed')}</Tag>
                            )}
                        </div>
                    );
                },
            },
        ];

        // 异常状态列（复用）
        const exceptionStatusColumn = {
            title: t('wms.exceptions.columns.exceptionStatus'),
            dataIndex: 'exceptionStatus',
            key: 'exceptionStatus',
            align: 'left' as const,
            render: (status: number) => {
                const config = getExceptionStatusConfig(status);
                return (
                    <Tag color={config.color}>{config.text}</Tag>
                );
            }
        };

        // 下一步动作列（带编辑功能，固定在最右边）
        const nextActionColumn = {
            title: t('wms.exceptions.columns.nextAction'),
            dataIndex: 'nextAction',
            key: 'nextAction',
            width: 180,
            fixed: 'right' as const,
            align: 'left' as const,
            render: (action: number, record: ExceptionOrderListItem) => {
                // 判断是否禁止编辑
                const disabled = isNextActionDisabled(record);

                // 禁止编辑的情况：只显示内容，不显示编辑按钮
                if (disabled) {
                    return <span style={{ whiteSpace: 'nowrap' }}>{getNextActionText(action)}</span>;
                }

                const isEditing = editingKey === record.id;

                if (isEditing) {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <Select
                                value={editingAction || undefined}
                                onChange={setEditingAction}
                                style={{ width: 120 }}
                                size="small"
                                options={ACTION_OPTIONS}
                                placeholder={t('wms.exceptions.filters.selectAction')}
                                allowClear={false}
                            />
                            <div style={{ display: 'flex', gap: 4 }}>
                                <Button
                                    icon={<CheckOutlined />}
                                    type="text"
                                    size="small"
                                    style={{ color: '#722ed1', background: 'transparent', padding: '4px' }}
                                    onClick={() => saveEdit(record)}
                                />
                                <Button
                                    icon={<CloseOutlined />}
                                    type="text"
                                    size="small"
                                    style={{ color: '#9254de', padding: '4px' }}
                                    onClick={cancelEdit}
                                />
                            </div>
                        </div>
                    );
                }

                return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ whiteSpace: 'nowrap' }}>{getNextActionText(action)}</span>
                        <Button
                            icon={<EditOutlined />}
                            type="text"
                            size="small"
                            style={{ color: '#722ed1', background: 'transparent', padding: '4px' }}
                            onClick={() => startEdit(record)}
                        />
                    </div>
                );
            },
        };

        // 异常时间列（复用）
        const exceptionTimeColumn = {
            title: t('wms.exceptions.columns.exceptionTime'),
            dataIndex: 'markExceptionAt',
            key: 'markExceptionAt',
            width: 140,
            align: 'left' as const,
            render: (timestamp: string) => timestamp ? formatTimestampToDateTime(timestamp) : '-',
        };

        // 仓库疑似丢失
        // 列顺序：运单号、运单状态、收件地址、客户、中心、仓库、DSP、司机、异常原因、异常状态、异常时间、异常标记人、尝试派送次数、操作指令
        if (currentTab === 'WAREHOUSE_SUSPECTED_LOST') {
            return [
                ...commonColumns,
                {
                    title: t('wms.exceptions.columns.parcelStatus'),
                    dataIndex: 'orderStatus',
                    key: 'orderStatus',
                    align: 'left',
                    render: (status: number) => getOrderStatusText(status),
                },
                {
                    title: t('wms.exceptions.columns.receiverAddr'),
                    dataIndex: 'recipientAddress',
                    key: 'recipientAddress',
                    width: 200,
                    align: 'left',
                    ellipsis: true,
                    render: (address: RecipientAddressType) => buildFullAddress(address) || '-',
                },
                {
                    title: t('wms.exceptions.columns.client'),
                    dataIndex: 'clientName',
                    key: 'clientName',
                    align: 'left',
                    render: (text) => text || '-',
                },
                {
                    title: t('wms.exceptions.columns.hub'),
                    dataIndex: 'hubName',
                    key: 'hubName',
                    align: 'left',
                    render: (text) => text || '-',
                },
                {
                    title: t('wms.exceptions.columns.whs'),
                    dataIndex: 'warehouseName',
                    key: 'warehouseName',
                    align: 'left',
                    render: (text) => text || '-',
                },
                {
                    title: t('wms.exceptionorder.columns.dspName'),
                    dataIndex: 'dspName',
                    key: 'dspName',
                    align: 'left',
                    render: (text) => text || '-',
                },
                {
                    title: t('wms.exceptionorder.columns.driverName'),
                    dataIndex: 'driverName',
                    key: 'driverName',
                    align: 'left',
                    render: (text) => text || '-',
                },
                {
                    title: t('wms.exceptions.columns.exceptionReason'),
                    dataIndex: 'exceptionReasonCode',
                    key: 'exceptionReasonCode',
                    align: 'left',
                    render: (code: string) => getExceptionReasonTextByCode(code),
                },
                exceptionStatusColumn,
                exceptionTimeColumn,
                {
                    title: t('wms.exceptionorder.columns.markExceptionOperatorName'),
                    dataIndex: 'markExceptionOperatorName',
                    key: 'markExceptionOperatorName',
                    align: 'left',
                    render: (text) => text || '-',
                },
                {
                    title: t('wms.exceptions.columns.deliveryAttempts'),
                    dataIndex: 'deliveryAttempts',
                    key: 'deliveryAttempts',
                    width: 100,
                    align: 'left',
                    render: (attempts: number) => (
                        <span className={attempts >= 3 ? 'text-red-600 font-bold' : ''}>
                            {attempts || 0}
                        </span>
                    ),
                },
                nextActionColumn,
            ];
        }

        // 司机退回
        // 基础列顺序与仓库疑似丢失一致，差异列插入到仓库之后
        if (currentTab === 'DRIVER_RETURN') {
            const driverReturnColumns: ColumnsType<ExceptionOrderListItem> = [
                ...commonColumns,
                {
                    title: t('wms.exceptions.columns.parcelStatus'),
                    dataIndex: 'orderStatus',
                    key: 'orderStatus',
                    align: 'left',
                    render: (status: number) => getOrderStatusText(status),
                },
                {
                    title: t('wms.exceptions.columns.receiverAddr'),
                    dataIndex: 'recipientAddress',
                    key: 'recipientAddress',
                    width: 200,
                    align: 'left',
                    ellipsis: true,
                    render: (address: RecipientAddressType) => buildFullAddress(address) || '-',
                },
                {
                    title: t('wms.exceptions.columns.client'),
                    dataIndex: 'clientName',
                    key: 'clientName',
                    align: 'left',
                    render: (text) => text || '-',
                },
                {
                    title: t('wms.exceptions.columns.hub'),
                    dataIndex: 'hubName',
                    key: 'hubName',
                    align: 'left',
                    render: (text) => text || '-',
                },
                {
                    title: t('wms.exceptions.columns.whs'),
                    dataIndex: 'warehouseName',
                    key: 'warehouseName',
                    align: 'left',
                    render: (text) => text || '-',
                },
            ];

            // 根据子Tab添加差异列（插入到仓库之后）
            // 全部在库: 库位号、存储编号
            if (currentSubTab === 'all') {
                driverReturnColumns.push(
                    {
                        title: t('wms.exceptionorder.columns.warehouseLocationCode'),
                        dataIndex: 'warehouseLocationCode',
                        key: 'warehouseLocationCode',
                        align: 'left',
                        render: (text) => text || '-',
                    },
                    {
                        title: t('wms.exceptionorder.columns.storageNumber'),
                        dataIndex: 'storageNumber',
                        key: 'storageNumber',
                        align: 'left',
                        render: (storageNumber: number | null) => storageNumber !== null && storageNumber !== undefined ? String(storageNumber).padStart(4, '0') : '-',
                    }
                );
            }

            // 已上架、待下架: 库位号、存储编号、上架时间、上架天数
            if (currentSubTab === 'putaway' || currentSubTab === 'pending_picking') {
                driverReturnColumns.push(
                    {
                        title: t('wms.exceptionorder.columns.warehouseLocationCode'),
                        dataIndex: 'warehouseLocationCode',
                        key: 'warehouseLocationCode',
                        align: 'left',
                        render: (text) => text || '-',
                    },
                    {
                        title: t('wms.exceptionorder.columns.storageNumber'),
                        dataIndex: 'storageNumber',
                        key: 'storageNumber',
                        align: 'left',
                        render: (storageNumber: number | null) => storageNumber !== null && storageNumber !== undefined ? String(storageNumber).padStart(4, '0') : '-',
                    },
                    {
                        title: t('wms.exceptionorder.columns.putawayAt'),
                        dataIndex: 'putawayAt',
                        key: 'putawayAt',
                        width: 140,
                        align: 'left',
                        render: (timestamp: string) => timestamp ? formatTimestampToDateTime(timestamp) : '-',
                    },
                    {
                        title: t('wms.exceptionorder.columns.putawayDays'),
                        dataIndex: 'putawayDays',
                        key: 'putawayDays',
                        align: 'left',
                        render: (days: number) => days ?? '-',
                    }
                );
            }

            // 已下架: 库位号、存储编号、下架时间
            if (currentSubTab === 'picked') {
                driverReturnColumns.push(
                    {
                        title: t('wms.exceptionorder.columns.warehouseLocationCode'),
                        dataIndex: 'warehouseLocationCode',
                        key: 'warehouseLocationCode',
                        align: 'left',
                        render: (text) => text || '-',
                    },
                    {
                        title: t('wms.exceptionorder.columns.storageNumber'),
                        dataIndex: 'storageNumber',
                        key: 'storageNumber',
                        align: 'left',
                        render: (storageNumber: number | null) => storageNumber !== null && storageNumber !== undefined ? String(storageNumber).padStart(4, '0') : '-',
                    },
                    {
                        title: t('wms.exceptionorder.columns.pickingAt'),
                        dataIndex: 'pickingAt',
                        key: 'pickingAt',
                        width: 140,
                        align: 'left',
                        render: (timestamp: string) => timestamp ? formatTimestampToDateTime(timestamp) : '-',
                    }
                );
            }

            // 继续添加剩余的基础列
            driverReturnColumns.push(
                {
                    title: t('wms.exceptionorder.columns.dspName'),
                    dataIndex: 'dspName',
                    key: 'dspName',
                    align: 'left',
                    render: (text) => text || '-',
                },
                {
                    title: t('wms.exceptionorder.columns.driverName'),
                    dataIndex: 'driverName',
                    key: 'driverName',
                    align: 'left',
                    render: (text) => text || '-',
                },
                {
                    title: t('wms.exceptions.columns.exceptionReason'),
                    dataIndex: 'exceptionReasonCode',
                    key: 'exceptionReasonCode',
                    align: 'left',
                    render: (code: string) => getExceptionReasonTextByCode(code),
                },
                exceptionStatusColumn,
                exceptionTimeColumn,
                {
                    title: t('wms.exceptionorder.columns.markExceptionOperatorName'),
                    dataIndex: 'markExceptionOperatorName',
                    key: 'markExceptionOperatorName',
                    align: 'left',
                    render: (text) => text || '-',
                },
                {
                    title: t('wms.exceptions.columns.deliveryAttempts'),
                    dataIndex: 'deliveryAttempts',
                    key: 'deliveryAttempts',
                    width: 100,
                    align: 'left',
                    render: (attempts: number) => (
                        <span className={attempts >= 3 ? 'text-red-600 font-bold' : ''}>
                            {attempts || 0}
                        </span>
                    ),
                },
                nextActionColumn
            );

            return driverReturnColumns;
        }

        // 其他异常（丢件、长时间未找到、破损、错分）
        // 列顺序与仓库疑似丢失完全一致
        return [
            ...commonColumns,
            {
                title: t('wms.exceptions.columns.parcelStatus'),
                dataIndex: 'orderStatus',
                key: 'orderStatus',
                align: 'left',
                render: (status: number) => getOrderStatusText(status),
            },
            {
                title: t('wms.exceptions.columns.receiverAddr'),
                dataIndex: 'recipientAddress',
                key: 'recipientAddress',
                width: 200,
                align: 'left',
                ellipsis: true,
                render: (address: RecipientAddressType) => buildFullAddress(address) || '-',
            },
            {
                title: t('wms.exceptions.columns.client'),
                dataIndex: 'clientName',
                key: 'clientName',
                align: 'left',
                render: (text) => text || '-',
            },
            {
                title: t('wms.exceptions.columns.hub'),
                dataIndex: 'hubName',
                key: 'hubName',
                align: 'left',
                render: (text) => text || '-',
            },
            {
                title: t('wms.exceptions.columns.whs'),
                dataIndex: 'warehouseName',
                key: 'warehouseName',
                align: 'left',
                render: (text) => text || '-',
            },
            {
                title: t('wms.exceptionorder.columns.dspName'),
                dataIndex: 'dspName',
                key: 'dspName',
                align: 'left',
                render: (text) => text || '-',
            },
            {
                title: t('wms.exceptionorder.columns.driverName'),
                dataIndex: 'driverName',
                key: 'driverName',
                align: 'left',
                render: (text) => text || '-',
            },
            {
                title: t('wms.exceptions.columns.exceptionReason'),
                dataIndex: 'exceptionReasonCode',
                key: 'exceptionReasonCode',
                align: 'left',
                render: (code: string) => getExceptionReasonTextByCode(code),
            },
            exceptionStatusColumn,
            exceptionTimeColumn,
            {
                title: t('wms.exceptionorder.columns.markExceptionOperatorName'),
                dataIndex: 'markExceptionOperatorName',
                key: 'markExceptionOperatorName',
                align: 'left',
                render: (text) => text || '-',
            },
            {
                title: t('wms.exceptions.columns.deliveryAttempts'),
                dataIndex: 'deliveryAttempts',
                key: 'deliveryAttempts',
                width: 100,
                align: 'left',
                render: (attempts: number) => (
                    <span className={attempts >= 3 ? 'text-red-600 font-bold' : ''}>
                        {attempts || 0}
                    </span>
                ),
            },
            nextActionColumn,
        ];
    };

    // 行样式：超期标红（仅在第一个Tab时，后端 isOverdue 已限制为 CREATED 状态）
    const rowClassName = (record: ExceptionOrderListItem) => {
        const shouldHighlight = currentTab === 'WAREHOUSE_SUSPECTED_LOST' && record.isOverdue;
        return shouldHighlight ? 'exception-order-overdue-row' : '';
    };

    return (
        <>
            {/* 超期行样式，确保固定列也应用背景色 */}
            <style>{`
                .exception-order-overdue-row td {
                    background-color: #fef2f2 !important;
                }
                .exception-order-overdue-row:hover td {
                    background-color: #fee2e2 !important;
                }
            `}</style>
            <Table
                rowSelection={rowSelection}
                columns={getColumns()}
                dataSource={data}
                loading={loading}
                rowKey="id"
                rowClassName={rowClassName}
                size="middle"
                className="mb-4"
                tableLayout="auto"
                pagination={false}
                scroll={{ x: 'max-content' }}
            />
            {/* 分页 */}
            <div className="flex justify-between items-center">
                <div className="text-gray-500">
                    {t('wms.exceptions.pagination.exceptionsFound', { count: pagination.total })}
                </div>
                <Pagination
                    current={pagination.current}
                    total={pagination.total}
                    pageSize={pagination.pageSize}
                    size="small"
                    showSizeChanger
                    onChange={pagination.onChange}
                    showQuickJumper
                />
            </div>
        </>
    );
};

export default ExceptionOrderTable;
